import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	Button,
	Typography,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	Menu,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
} from "@mui/material";
import AppLayout from "../../components/AppLayout";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ImportarClientesDialog from "./ImportarClientesDialog";
import { ClienteService } from "../../service/cliente_service";

function Clientes() {
	const navigate = useNavigate();
	const [clientes, setClientes] = useState([]);
	const [loading, setLoading] = useState(true);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [total, setTotal] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [anchorEl, setAnchorEl] = useState(null);
	const [selectedItem, setSelectedItem] = useState(null);
	const [openDialog, setOpenDialog] = useState(false);
	const [itemToDelete, setItemToDelete] = useState(null);
	const [openImportar, setOpenImportar] = useState(false);

	const unidadeDistribuidoraId = localStorage.getItem("selected_unidade_id");

	const loadClientes = useCallback(async () => {
		setLoading(true);
		try {
			const clienteService = new ClienteService();
			const result = await clienteService.getAll(unidadeDistribuidoraId, page + 1, rowsPerPage, searchTerm);
			setClientes(result.data || []);
			setTotal(result.total || 0);
		} catch (error) {
			console.error("Erro ao carregar clientes:", error);
			alert(error.message || "Erro ao carregar clientes");
		} finally {
			setLoading(false);
		}
	}, [unidadeDistribuidoraId, page, rowsPerPage, searchTerm]);

	useEffect(() => {
		if (unidadeDistribuidoraId) {
			loadClientes();
		}
	}, [unidadeDistribuidoraId, loadClientes]);

	// Debounced search effect
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			setPage(0);
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [searchTerm]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleSearchChange = (event) => {
		setSearchTerm(event.target.value);
	};

	const handleOpenMenu = (event, item) => {
		setAnchorEl(event.currentTarget);
		setSelectedItem(item);
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
		setSelectedItem(null);
	};

	const handleEdit = () => {
		if (selectedItem) {
			navigate(`/clientes/cadastro/${selectedItem.id}`);
		}
		handleCloseMenu();
	};

	const handleDeleteClick = () => {
		setItemToDelete(selectedItem);
		setOpenDialog(true);
		handleCloseMenu();
	};

	const handleConfirmDelete = async () => {
		if (itemToDelete) {
			try {
				const clienteService = new ClienteService();
				await clienteService.changeVisibility(itemToDelete.id);
				setOpenDialog(false);
				setItemToDelete(null);
				loadClientes();
			} catch (error) {
				console.error("Erro ao excluir cliente:", error);
				alert(error.message || "Erro ao excluir cliente");
			}
		}
	};

	const handleCancelDelete = () => {
		setOpenDialog(false);
		setItemToDelete(null);
	};

	const formatCpfCnpj = (value) => {
		if (!value) return "-";
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	};

	return (
		<AppLayout currentPage="clientes">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 3,
						}}
					>
						<Typography
							variant="h4"
							sx={{
								color: "#666",
								fontWeight: 600,
							}}
						>
							Clientes
						</Typography>
						<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
							<TextField
								size="small"
								placeholder="Buscar..."
								value={searchTerm}
								onChange={handleSearchChange}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
								sx={{
									width: "300px",
									"& .MuiOutlinedInput-root": {
										borderRadius: "8px",
									},
								}}
							/>
							<Button
								variant="outlined"
								startIcon={<UploadFileIcon />}
								onClick={() => setOpenImportar(true)}
								sx={{
									borderColor: "#1a9e8f",
									color: "#1a9e8f",
									"&:hover": { borderColor: "#138a7d", color: "#138a7d", bgcolor: "rgba(26, 158, 143, 0.06)" },
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Importar planilha
							</Button>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={() => navigate("/clientes/cadastro")}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
									},
									textTransform: "none",
									px: 3,
									py: 1,
									borderRadius: "8px",
								}}
							>
								Novo Cliente
							</Button>
						</Box>
					</Box>

					<Paper
						sx={{
							bgcolor: "white",
							borderRadius: "12px",
							boxShadow: "0 2px 8px rgba(0, 0, 0, 0.05)",
							overflow: "hidden",
						}}
					>
						{loading ? (
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "400px",
								}}
							>
								<CircularProgress sx={{ color: "#1a9e8f" }} />
							</Box>
						) : clientes.length === 0 ? (
							<Box
								sx={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									minHeight: "400px",
									gap: 2,
									p: 4,
								}}
							>
								<Typography
									variant="h6"
									sx={{ color: "#999", textAlign: "center" }}
								>
									Nenhum cliente encontrado
								</Typography>
								<Typography
									variant="body2"
									sx={{ color: "#999", textAlign: "center" }}
								>
									{searchTerm
										? "Tente fazer uma busca diferente"
										: 'Clique no botão "Novo Cliente" para começar'}
								</Typography>
							</Box>
						) : (
							<>
								<TableContainer>
									<Table>
										<TableHead>
											<TableRow sx={{ bgcolor: "#f5f5f5" }}>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Nome
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													CPF/CNPJ
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Email
												</TableCell>
												<TableCell sx={{ fontWeight: 600, color: "#666" }}>
													Cidade/UF
												</TableCell>
												<TableCell
													sx={{ fontWeight: 600, color: "#666", width: "80px" }}
													align="center"
												>
													Ações
												</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{clientes.map((cliente) => (
												<TableRow
													key={cliente.id}
													sx={{
														"&:hover": {
															bgcolor: "#f9f9f9",
														},
													}}
												>
													<TableCell>
														<Typography variant="body2" sx={{ fontWeight: 500 }}>
															{cliente.nome}
														</Typography>
														{cliente.nomeFantasia && (
															<Typography
																variant="caption"
																sx={{ color: "#999", display: "block" }}
															>
																{cliente.nomeFantasia}
															</Typography>
														)}
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{formatCpfCnpj(cliente.cpfCnpj)}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{cliente.email || "-"}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="body2">
															{cliente.endereco?.cidade && cliente.endereco?.uf
																? `${cliente.endereco.cidade} - ${cliente.endereco.uf}`
																: "-"}
														</Typography>
													</TableCell>
													<TableCell align="center">
														<IconButton
															size="small"
															onClick={(e) => handleOpenMenu(e, cliente)}
															sx={{ color: "#666" }}
														>
															<MoreVertIcon />
														</IconButton>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
								<TablePagination
									component="div"
									count={total}
									page={page}
									onPageChange={handleChangePage}
									rowsPerPage={rowsPerPage}
									onRowsPerPageChange={handleChangeRowsPerPage}
									rowsPerPageOptions={[5, 10, 25, 50]}
									labelRowsPerPage="Linhas por página:"
									labelDisplayedRows={({ from, to, count }) =>
										`${from}-${to} de ${count}`
									}
									sx={{
										borderTop: "1px solid #e0e0e0",
										".MuiTablePagination-select": {
											borderRadius: "4px",
										},
									}}
								/>
							</>
						)}
					</Paper>
			</Box>

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleCloseMenu}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
			>
				<MenuItem onClick={handleEdit}>
					<EditIcon fontSize="small" sx={{ mr: 1, color: "#1a9e8f" }} />
					Editar
				</MenuItem>
				<MenuItem onClick={handleDeleteClick}>
					<DeleteIcon fontSize="small" sx={{ mr: 1, color: "#ff4d4d" }} />
					Excluir
				</MenuItem>
			</Menu>

			<Dialog open={openDialog} onClose={handleCancelDelete}>
				<DialogTitle>Confirmar Exclusão</DialogTitle>
				<DialogContent>
					<Typography>
						Tem certeza que deseja excluir o cliente "
						{itemToDelete?.nome}"?
					</Typography>
					<Typography variant="caption" sx={{ color: "#999", mt: 1, display: "block" }}>
						Esta ação não poderá ser desfeita.
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCancelDelete} sx={{ color: "#666" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleConfirmDelete}
						variant="contained"
						sx={{
							bgcolor: "#ff4d4d",
							"&:hover": {
								bgcolor: "#e64545",
							},
						}}
					>
						Confirmar
					</Button>
				</DialogActions>
			</Dialog>

			<ImportarClientesDialog
				open={openImportar}
				onClose={() => setOpenImportar(false)}
				onImportado={() => {
					setPage(0);
					loadClientes();
				}}
				unidadeDistribuidoraId={unidadeDistribuidoraId}
			/>
		</AppLayout>
	);
}

export default Clientes;
